import {Page} from '@app/views/databrowsing/model/page';
import {IEntity} from '@app/views/databrowsing/model/common.data.models';
import {EntityAttributeMetadata} from '@app/core/model/entity-attribute-metadata';
import {
    DataAttribute,
    OneRosterExtensionSufix,
    SchoolIdPropertyNames
} from '@app/views/admin/application-settings/models/data-attribute.model';
import {RestrictionFilter} from '@app/core/model/filtering/restriction-filter';
import {DataBrowsingFilterPredicate, DataBrowsingFilterPredicateMap, FilterPredicate} from '@app/core/model/filtering/filter-predicate';
import {PagedData} from '@app/views/databrowsing/model/paged-data';
import {DatabrowsingOptions} from '@app/views/databrowsing/model/databrowsing-options.model';
import {IDataBrowser} from '@app/views/databrowsing/model/idata-browser.model';
import {Observable} from 'rxjs';

export function createBrowserEntityViewInstance<EntityModelT extends IEntity>(EntityModelClass: new () => EntityModelT,
    dataBrowser: IDataBrowser,
    title: string,
    singular,
    entity: string,
    eduToneEntity: string): BrowserEntityView<EntityModelT> {
    return new BrowserEntityView<EntityModelT>(dataBrowser, title, singular, entity, eduToneEntity);
}

export interface IBrowserEntityView {
    title: string;
    singular;
    entity: string;
    eduToneEntity: string;
}

export class BrowserEntityView<EntityModelT> implements IBrowserEntityView {
    options: DatabrowsingOptions;
    page: Page;
    rows: IEntity[];
    total: number;
    absTotal: number;
    allColumns: EntityAttributeMetadata[];
    idColumns: EntityAttributeMetadata[];
    attributes: DataAttribute[];
    changed = false;
    highlited = false;
    isLoading = false;

    constructor(private dataBrowser: IDataBrowser, public title: string, public singular, public entity: string, public eduToneEntity: string) {
        this.absTotal = 0;
        this.page = new Page();
        this.page.entity = this.entity;
        this.page.sort = '';
        this.page.sorts = [];
        this.setToFirstPage();
    }

    setFilteredTotal(value: number) {
        const pv = this.total;
        this.total = value;
        if ((typeof pv !== 'undefined' && pv !== this.total) || this.absTotal !== this.total) {
            // this.blinkTab();
        }
    }

    getFilterParams = function () {
        return this.dataBrowser
            .getAllCompletedFilters()
            .filter((a: RestrictionFilter) => a.entity.sourceType === this.entity)
            .reduce((filters, f: RestrictionFilter) => {
                let value = f.value;
                if (f.predicate.name == 'IsEmpty') {
                    value = 'IS_EMPTY';
                }
                if (f.predicate.name == 'IsNotEmpty') {
                    value = 'IS_NOT_EMPTY';
                }
                filters[f.attribute.propertyName] = value;
                return filters;
            }, {});
    };

    getOneRosterFilter = function (): string {
        return this.dataBrowser
            .getAllCompletedFilters()
            .filter((filterItem: RestrictionFilter) => filterItem.entity.sourceType === this.entity)
            .map((filterItem: RestrictionFilter) => {
                let propertyName = filterItem.attribute.propertyName;
                if (filterItem.attribute.entityTypeDenom !== 'School'
                    && ((filterItem.attribute.friendlyName === 'Primary School Name')
                        || (filterItem.attribute.friendlyName === 'School Name'))) {
                    const idColumn = this.idColumns.find((column: EntityAttributeMetadata) =>
                        column.paramName.substr(-10) === 'schoolGuid' || column.paramName.substr(-8) === 'schoolId'
                    );
                    if (idColumn) {
                        propertyName = idColumn.paramName;
                    } else {
                        if (filterItem.attribute.friendlyName === 'School Name') {
                            propertyName = 'schoolId';
                        }
                    }
                }

                let predicate = DataBrowsingFilterPredicateMap.get(filterItem.predicate.name);
                let value = filterItem.predicate.isRequireArguments ? filterItem.value : '';

                // Use equals / not equals predicate instead of contains
                const attribute = this.attributes.find((attr: DataAttribute) => attr.propertyName === propertyName);
                const valueTypeIsGuid = attribute &&
                    ((attribute.propertyTypeDenom.substr(-4) === 'Guid')
                     || (attribute.propertyTypeDenom.substr(-2) === 'Id'));
                const isSchoolIdFilter = (SchoolIdPropertyNames.indexOf(attribute.propertyName) > -1)
                    && (filterItem.entity.edutoneEntity !== 'School');

                if ((predicate === DataBrowsingFilterPredicate.CONTAINS) || (predicate === DataBrowsingFilterPredicate.NOT_CONTAINS)) {
                    if (valueTypeIsGuid && this.isGuidValue(value)) {
                        if (isSchoolIdFilter) {
                            if (predicate === DataBrowsingFilterPredicate.CONTAINS) {
                                predicate = DataBrowsingFilterPredicate.IN_ARRAY;
                            } else if (predicate === DataBrowsingFilterPredicate.NOT_CONTAINS) {
                                predicate = DataBrowsingFilterPredicate.NOT_IN_ARRAY;
                            }
                        } else {
                            predicate = (predicate === DataBrowsingFilterPredicate.CONTAINS)
                                ? DataBrowsingFilterPredicate.EQUALS
                                : DataBrowsingFilterPredicate.NOT_EQUAL;
                        }
                    } else {
                        value = `%${value}%`;
                    }
                }

                if (isSchoolIdFilter && valueTypeIsGuid && (value === '')) {
                    if (predicate === DataBrowsingFilterPredicate.EQUALS) {
                        predicate = DataBrowsingFilterPredicate.IS_EMPTY_ARRAY;
                    }
                    if (predicate === DataBrowsingFilterPredicate.NOT_EQUAL) {
                        predicate = DataBrowsingFilterPredicate.IS_NOT_EMPTY_ARRAY;
                    }
                }

                if (filterItem.predicate.name === FilterPredicate.IS_TRUE) {
                    value = 'true';
                } else if (filterItem.predicate.name === FilterPredicate.IS_FALSE) {
                    value = 'false';
                } else if ((filterItem.predicate.name === FilterPredicate.IS_FUTURE) || (filterItem.predicate.name === FilterPredicate.IS_PAST)) {
                    value = new Date().toISOString().slice(0, 10);
                }

                if (filterItem.attribute.isExtensionField) {
                    const extensionPropertyName = (filterItem.attribute.extensionPropertyName || '').trim();
                    propertyName = propertyName.replace(OneRosterExtensionSufix, '.' + extensionPropertyName);
                }

                return `${propertyName}${predicate}'${value}'`;
            })
            .join(' AND ');
    };

    isGuidValue(value: string): boolean {
        const regexGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        return regexGuid.test(value);
    }

    setToFirstPage() {
        this.page.pageNumber = 1;
        this.page.size = 20;
        this.page.limit = this.page.size;
    }

    findRowByGuid(guid: string) {
        const keyName = this.page.idParamName;
        return this.rows.filter(r => r[keyName] === guid)[0];
    }

    blinkTab() {
        this.highlited = true;
        const timerId = setInterval(() => {
            this.changed = true;
            setTimeout(() => {
                this.changed = false;
                console.log(this.changed);
            }, 400);
        }, 800);
        setTimeout(() => {
            clearInterval(timerId);
            this.highlited = false;
        }, 2400);
    }

    getData(useVendorData: boolean = false): Observable<PagedData<EntityModelT>> {
        return this.dataBrowser.getData<EntityModelT>(this.page, useVendorData);
    }
}
